import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { MapPin, ArrowSquareOut, Spinner, UsersThree, CircleNotch } from '@phosphor-icons/react';
import { alumniService, type AlumniProfile } from '../../../services/alumni.service';
import { useHaptic } from '../../../hooks/useHaptic';
import { Sheet } from '../../../components/ui/Sheet';
import { Button } from '../../../components/ui/Button';
import { formatIndianNumber } from '../../../utils/currencyFormatter';
import { toast } from 'sonner';

const safeStr = (val: unknown, fallback = 'N/A'): string => {
  if (val === null || val === undefined) return fallback;
  if (typeof val === 'object') {
    const keys = Object.keys(val as object);
    if (keys.length === 1 && keys[0].startsWith('$')) return String(Object.values(val as object)[0]);
    return JSON.stringify(val);
  }
  const s = String(val).trim();
  return s === '' ? fallback : s;
};

/** Safely format a date value that may be an ISO string, timestamp, or MongoDB $date object. */
const safeDate = (val: unknown): string => {
  if (val === null || val === undefined) return 'N/A';
  let raw: unknown = val;
  if (typeof val === 'object' && val !== null && '$date' in val) raw = (val as Record<string, unknown>).$date;
  try {
    const d = new Date(raw as string | number);
    if (isNaN(d.getTime())) return String(raw);
    return d.toLocaleDateString();
  } catch {
    return String(raw);
  }
};

type Donation = NonNullable<AlumniProfile['monday_data']>[number] & { stage?: string };

const SIMILAR_DONOR_LIMIT = 3;

interface SimilarDonorState {
  data: AlumniProfile[];
  loading: boolean;
  error: string | null;
}

const getAlumniDisplayName = (alumni: AlumniProfile): string => {
  return alumni.name
    || alumni.source_info?.current_name
    || alumni.source_info?.csv_name
    || alumni.linkedin_info?.name
    || alumni.linkedin_profile?.name
    || 'Unknown Alumni';
};

const getAlumniProfileLookupId = (alumni: AlumniProfile): string => {
  return alumni._id
    || alumni.donor_id
    || alumni.source_info?.alumni_id
    || alumni.source_info?.alumni_csr_id
    || '';
};

const getAlumniProfileCacheKey = (alumni: AlumniProfile): string => {
  return alumni.donor_id
    || alumni.source_info?.alumni_id
    || alumni.source_info?.alumni_csr_id
    || alumni._id
    || '';
};


interface AlumniQuickViewSheetProps {
  /** Donor to show; null keeps the sheet closed. */
  donorId: string | null;
  onClose: () => void;
  /** The donor's full profile, when the caller already has it; skips the fetch. */
  initialProfile?: AlumniProfile;
  /** Called with each profile the sheet fetches, so the caller can keep it. */
  onProfileLoaded?: (donorId: string, profile: AlumniProfile) => void;
  /** Called when the displayed donor changes (e.g. similar donor clicked or viewing profile). */
  onDonorChange?: (donorId: string) => void;
}

/**
 * Slide-over summary of one alumnus (contact, VALS scores, donations, similar
 * donors), so a list can be browsed donor by donor without leaving the page.
 * "View Full Profile" goes to the detail page.
 */
export function AlumniQuickViewSheet({ donorId, onClose, initialProfile, onProfileLoaded, onDonorChange }: AlumniQuickViewSheetProps) {
  // Portalled to <body>: the sheet is position:fixed, and a transformed or
  // filtered ancestor (e.g. an animating dashboard card) would otherwise pin
  // it inside that ancestor instead of the viewport.
  return createPortal(
    <Sheet
      isOpen={donorId !== null}
      onClose={onClose}
      title="Alumni Profile Details"
      maxWidth="max-w-xl"
    >
      {/* Keyed so each donor starts from fresh state. */}
      {donorId && (
        <QuickViewBody
          key={donorId}
          donorId={donorId}
          initialProfile={initialProfile}
          onProfileLoaded={onProfileLoaded}
          onDonorChange={onDonorChange}
        />
      )}
    </Sheet>,
    document.body,
  );
}

function QuickViewBody({ donorId, initialProfile, onProfileLoaded, onDonorChange }: {
  donorId: string;
  initialProfile?: AlumniProfile;
  onProfileLoaded?: (donorId: string, profile: AlumniProfile) => void;
  onDonorChange?: (donorId: string) => void;
}) {
  const navigate = useNavigate();
  const { lightTap } = useHaptic();

  // Full profiles fetched so far, by the id they were asked for. Clicking a
  // similar donor adds to it, so going back and forth never refetches.
  const [profiles, setProfiles] = useState<Record<string, AlumniProfile>>(
    () => (initialProfile ? { [donorId]: initialProfile } : {}),
  );
  // The profile on screen (null until the first one loads) and the one asked
  // for last. They differ while a fetch is in flight; the current profile
  // stays up until the next one arrives, as with the old drawer.
  const [shownId, setShownId] = useState<string | null>(initialProfile ? donorId : null);
  const [wantedId, setWantedId] = useState(donorId);
  const [loadFailed, setLoadFailed] = useState(false);
  const [showValsAnalysis, setShowValsAnalysis] = useState(false);
  const [similarDonorsById, setSimilarDonorsById] = useState<Record<string, SimilarDonorState>>({});
  const similarDonorRequestsRef = useRef<Set<string>>(new Set());

  const profile = shownId ? profiles[shownId] ?? null : null;
  const isDrawerLoading = !profile && !loadFailed;
  const loadingSimilarProfileKey = profile && wantedId !== shownId ? wantedId : null;

  const notifyLoaded = useEffectEvent((id: string, full: AlumniProfile) => {
    onProfileLoaded?.(id, full);
  });

  // Fetch the profile asked for last, then show it — unless something else
  // was asked for meanwhile, in which case it is only kept for later.
  useEffect(() => {
    if (wantedId === shownId) return;
    let stillWanted = true;
    alumniService.getAlumniById(wantedId)
      .then((full) => {
        setProfiles((prev) => ({ ...prev, [wantedId]: full }));
        notifyLoaded(wantedId, full);
        if (stillWanted) setShownId(wantedId);
      })
      .catch((err) => {
        console.error('Failed to load alumni profile:', err);
        if (!stillWanted) return;
        if (shownId === null) {
          setLoadFailed(true);
        } else {
          // A similar donor failed to load: stay on the current one.
          toast.error("Couldn't load that profile.");
          setWantedId(shownId);
        }
      });
    return () => {
      stillWanted = false;
    };
  }, [wantedId, shownId]);

  const handleSimilarDonorSelect = (item: AlumniProfile) => {
    lightTap();
    const id = item.donor_id || getAlumniProfileLookupId(item);
    if (!id) return;
    setWantedId(id);
    if (profiles[id]) setShownId(id);
    onDonorChange?.(id);
  };

  // Similar donors for whichever profile is showing, fetched once per profile.
  useEffect(() => {
    if (!profile) return;

    const lookupId = getAlumniProfileLookupId(profile);
    const cacheKey = getAlumniProfileCacheKey(profile);

    if (!lookupId || !cacheKey || similarDonorsById[cacheKey] || similarDonorRequestsRef.current.has(cacheKey)) {
      return;
    }

    // No "loading" entry is written first: a missing entry already renders
    // as loading.
    similarDonorRequestsRef.current.add(cacheKey);
    alumniService.getSimilarAlumni(lookupId, SIMILAR_DONOR_LIMIT)
      .then((res) => {
        setSimilarDonorsById((prev) => ({
          ...prev,
          [cacheKey]: {
            data: Array.isArray(res) ? res : [],
            loading: false,
            error: null,
          },
        }));
      })
      .catch((err: Error) => {
        console.error(`Failed to load similar donors for ${lookupId}:`, err);
        setSimilarDonorsById((prev) => ({
          ...prev,
          [cacheKey]: {
            data: [],
            loading: false,
            error: err.message || 'Unable to load similar donors',
          },
        }));
      })
      .finally(() => {
        similarDonorRequestsRef.current.delete(cacheKey);
      });
  }, [profile, similarDonorsById]);

  return (
    <>
      {isDrawerLoading && (
        <div className="flex flex-col items-center justify-center h-64 gap-3">
          <CircleNotch size={32} className="animate-spin text-accent" />
          <p className="text-sm text-secondary font-medium">Loading profile…</p>
        </div>
      )}
      {loadFailed && (
        <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
          <p className="text-sm font-semibold text-rose-500">Couldn't load this profile.</p>
          <Button
            variant="outline"
            onClick={() => {
              lightTap();
              onDonorChange?.(donorId);
              navigate(`/alumni/${donorId}`, { state: { from: 'constellation' } });
            }}
            className="py-1 px-3 text-[10px] font-bold h-auto"
          >
            Open Full Profile
          </Button>
        </div>
      )}
      {profile && (() => {

        const sInfo = profile.source_info ?? {} as NonNullable<typeof profile.source_info>;
        const lProfile = profile.linkedin_profile ?? {} as NonNullable<typeof profile.linkedin_profile>;
        const lInfo = profile.linkedin_info ?? {} as NonNullable<typeof profile.linkedin_info>;

        const rawName = sInfo.current_name || sInfo.csv_name || lInfo.name || lProfile.name;
        const name = typeof rawName === 'string' && rawName.trim() !== '' ? rawName.trim() : safeStr(rawName, 'Alumni');

        const initials = name
          .split(' ')
          .filter(Boolean)
          .slice(0, 2)
          .map((n: string) => n[0] ?? '')
          .join('')
          .toUpperCase() || 'A';

        const rawLocation = sInfo.work_city
          ? `${safeStr(sInfo.work_city)}${sInfo.person_account_work_address_country_territory ? `, ${safeStr(sInfo.person_account_work_address_country_territory)}` : ''}`
          : (lInfo.location || lInfo.city || lProfile.location || lProfile.city || 'N/A');
        const location = typeof rawLocation === 'string' ? rawLocation : safeStr(rawLocation);
        const imageUrl = lInfo.avatar || lInfo.photo || lInfo.profilePic;
        const selectedCacheKey = getAlumniProfileCacheKey(profile);
        const selectedSimilarState = selectedCacheKey ? similarDonorsById[selectedCacheKey] : undefined;
        const selectedSimilarDonors = (selectedSimilarState?.data || []).filter((item) => {
          const itemId = item.donor_id || item.source_info?.alumni_id || item.source_info?.alumni_csr_id || item._id;
          return itemId !== selectedCacheKey && item._id !== profile._id;
        });

        return (
          <div className="space-y-6 font-sans">
            {/* Profile Card Header */}
            <div className="flex items-start gap-4">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={name}
                  className="w-14 h-14 rounded-2xl object-cover shadow-md shrink-0"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random`;
                  }}
                />
              ) : (
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-accent to-accent-hover text-white font-black text-xl flex items-center justify-center shadow-md shrink-0 select-none">
                  {initials}
                </div>
              )}
              <div className="space-y-1">
                <h3 className="font-outfit font-extrabold text-2xl text-slate-900 leading-tight">
                  {name}
                </h3>
                <p className="font-bold text-slate-800 text-xs">
                  {safeStr(sInfo.designation, 'Alumni')}
                </p>
                <p className="text-[10px] text-slate-500 font-semibold mt-0.5">
                  at <span className="font-bold text-slate-700">{safeStr(sInfo.company)}</span>
                </p>
              </div>
            </div>

            {/* Status Tags */}
            <div className="flex flex-wrap gap-2">
              {sInfo.potential_donor && (
                <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 border border-emerald-100 px-2.5 py-1 rounded-full uppercase tracking-wider">
                  Potential Donor
                </span>
              )}

              {sInfo.batch && (
                <span className="text-[9px] font-bold text-accent bg-accent-light border border-accent/20 px-2.5 py-1 rounded-full uppercase tracking-wider">
                  Class of {safeStr(sInfo.batch)}
                </span>
              )}
              <span className="text-[9px] font-mono font-bold text-slate-500 bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-full uppercase tracking-wider">
                ID: {profile.donor_id || profile.source_info?.alumni_csr_id || 'N/A'}
              </span>

              <Button
                variant="primary"
                onClick={() => {
                  lightTap();
                  const id = profile.donor_id || profile._id;
                  if (id) {
                    onDonorChange?.(id);
                    navigate(`/alumni/${id}`, { state: { from: 'constellation' } });
                  }
                }}
                className="py-1 px-3 text-[10px] font-bold h-auto shadow-xs w-full mt-2"
              >
                View Full Profile
              </Button>
            </div>

            <hr className="border-slate-100" />

            {/* Contact & Professional Location */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">Location</span>
                <div className="flex items-center gap-1.5 text-xs font-bold text-slate-700">
                  <MapPin size={14} className="text-slate-455 shrink-0" />
                  {location}
                </div>
              </div>

              <div>
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">Education</span>
                <div className="text-xs font-bold text-slate-700 leading-snug">
                  {safeStr(sInfo.degree)}
                  <span className="text-[10px] text-slate-400 block font-semibold mt-0.5">{safeStr(sInfo.department)}</span>
                </div>
              </div>
            </div>

            {/* CRM Links */}
            <div className="grid grid-cols-2 gap-4">
              {(sInfo.salesforce_url || sInfo.salesforce_link) && (
                <div>
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Salesforce CRM</span>
                  <a
                    href={sInfo.salesforce_url || sInfo.salesforce_link || ''}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 mt-0.5 bg-accent/10 text-accent hover:bg-accent hover:text-white rounded-lg text-[10px] font-bold transition-all shadow-xs"
                  >
                    View CRM Record
                    <ArrowSquareOut size={13} weight="bold" />
                  </a>
                </div>
              )}

              {(lInfo.url || sInfo.linked_in || lProfile.url) && (
                <div>
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-1">LinkedIn Profile</span>
                  <a
                    href={lInfo.url || (sInfo.linked_in ? (sInfo.linked_in.startsWith('http') ? sInfo.linked_in : `https://${sInfo.linked_in}`) : lProfile.url) || ''}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-bold text-accent hover:text-accent-hover flex items-center gap-1 hover:underline transition-colors mt-0.5"
                  >
                    Open LinkedIn
                    <ArrowSquareOut size={13} weight="bold" />
                  </a>
                </div>
              )}
            </div>

            <hr className="border-slate-100" />

            {/* VALS psychographics analysis toggle */}
            {profile.vals_framework && (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <h4 className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">VALS Psychographics</h4>
                  <Button
                    variant="outline"
                    className="py-1 px-2.5 text-[9px] font-bold hover:border-accent hover:text-accent"
                    onClick={() => { lightTap(); setShowValsAnalysis(!showValsAnalysis); }}
                  >
                    {showValsAnalysis ? 'Hide VALS Scores' : 'Show VALS Scores'}
                  </Button>
                </div>

                {showValsAnalysis && (
                  <div className="bg-slate-50 border border-slate-205 rounded-2xl p-4 space-y-3 font-sans">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                      {(() => {
                        const scores = (profile.vals_framework as { scores?: Record<string, unknown> }).scores || {};
                        return [
                          { key: 'iitm_affinity', label: 'IITM Affinity' },
                          { key: 'research_advancement', label: 'Research Advancement' },
                          { key: 'founder_innovation', label: 'Founder Innovation' },
                          { key: 'legacy_recognition', label: 'Legacy Recognition' },
                          { key: 'student_development', label: 'Student Development' },
                          { key: 'social_national_impact', label: 'Social & National Impact' },
                          { key: 'public_influence', label: 'Public Influence' },
                          { key: 'philanthropic_history', label: 'Philanthropic History' }
                        ].map(({ key, label }) => {
                          const rawVal = scores[key];
                          let scoreVal = 5;
                          if (rawVal !== undefined && rawVal !== null) {
                            if (typeof rawVal === 'object' && 'score' in rawVal) {
                              scoreVal = Number((rawVal as { score?: unknown }).score) || 5;
                            } else {
                              scoreVal = Number(rawVal) || 5;
                            }
                          }
                          const pct = scoreVal * 10;
                          return (
                            <div key={key} className="space-y-1">
                              <div className="flex justify-between text-[10px] font-bold">
                                <span className="text-slate-600 font-semibold">{label}</span>
                                <span className="text-slate-900 font-bold">{scoreVal} / 10</span>
                              </div>
                              <div className="w-full bg-white h-1.5 rounded-full overflow-hidden border border-slate-200/50">
                                <div
                                  className="bg-accent h-full rounded-full transition-all duration-500"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}
                <hr className="border-slate-100" />
              </div>
            )}

            {/* Donation history */}
            {profile.monday_data && profile.monday_data.length > 0 && (() => {
              const grouped: Record<string, { total: number, payments: Donation[] }> = {};
              profile.monday_data.forEach((donation: Donation) => {
                const stage = donation.stage || 'Unknown Stage';
                if (!grouped[stage]) {
                  grouped[stage] = { total: 0, payments: [] };
                }
                grouped[stage].payments.push(donation);
                grouped[stage].total += (Number(donation.amount) || 0);
              });

              const sortedGroups = Object.entries(grouped).sort((a, b) => b[1].total - a[1].total);

              return (
                <div className="space-y-3">
                  <h4 className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Donations Record (Grouped by Stage)</h4>
                  <div className="space-y-4">
                    {sortedGroups.map(([stage, data], groupIdx) => (
                      <div key={groupIdx} className="space-y-2">
                        <div className="flex justify-between items-center bg-slate-100 px-3 py-2 rounded-lg border border-slate-200">
                          <span className="text-[10px] font-bold text-slate-700 uppercase tracking-wider">{stage}</span>
                          <span className="text-xs font-extrabold text-slate-900">
                            Total: INR {formatIndianNumber(data.total, '0')}
                          </span>
                        </div>
                        <div className="space-y-2 pl-2 border-l-2 border-slate-100 ml-1">
                          {data.payments.map((donation, idx) => (
                            <div key={idx} className="flex justify-between items-center p-2.5 border border-slate-100 rounded-xl bg-slate-50/50">
                              <div>
                                <p className="text-xs font-bold text-slate-800 leading-snug">{safeStr(donation.project)}</p>
                                <p className="text-[10px] text-slate-400 mt-0.5 font-semibold">{safeDate(donation.date)}</p>
                              </div>
                              <span className="font-extrabold text-[11px] text-slate-900 bg-white px-2 py-1 rounded-lg border border-slate-200 shrink-0">
                                {safeStr(donation.currency, 'INR')} {formatIndianNumber(Number(donation.amount), '0')}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-[9px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                  <UsersThree size={14} className="text-accent" weight="duotone" />
                  Similar Donors
                </h4>
                {selectedSimilarState?.data.length ? (
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">
                    {selectedSimilarDonors.length} match{selectedSimilarDonors.length !== 1 ? 'es' : ''}
                  </span>
                ) : null}
              </div>

              <div className="bg-slate-50 border border-slate-200/65 rounded-2xl p-3 space-y-2">
                {(!selectedSimilarState || selectedSimilarState.loading) ? (
                  <div className="flex items-center gap-2 text-[10px] text-slate-450 font-semibold py-1">
                    <Spinner size={13} className="animate-spin text-accent" />
                    Finding similar donors...
                  </div>
                ) : selectedSimilarState.error ? (
                  <p className="text-[10px] font-semibold text-rose-500">
                    Similar donors unavailable.
                  </p>
                ) : selectedSimilarDonors.length > 0 ? (
                  <div className="space-y-2">
                    {selectedSimilarDonors.map((item) => {
                      const itemName = getAlumniDisplayName(item);
                      const itemInfo = item.source_info;
                      const itemScore = item.composite_score !== undefined ? Math.round(item.composite_score) : null;
                      const itemMeta = [
                        itemInfo?.batch ? `Class of ${itemInfo.batch}` : null,
                        itemInfo?.company,
                      ].filter(Boolean).join(' • ');

                      const itemKey = getAlumniProfileCacheKey(item) || item._id || item.donor_id || itemName;
                      const isLoadingThisItem = loadingSimilarProfileKey === itemKey;

                      return (
                        <button
                          key={item._id || item.donor_id || itemName}
                          type="button"
                          disabled={isLoadingThisItem}
                          onClick={() => handleSimilarDonorSelect(item)}
                          className="w-full text-left rounded-xl border border-slate-200/70 bg-white hover:bg-accent-light hover:border-accent/20 px-3 py-2.5 transition-smooth disabled:opacity-60 disabled:cursor-wait"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="font-bold text-xs text-slate-800 truncate">{itemName}</p>
                              <p className="text-[10px] text-slate-450 font-semibold mt-0.5 truncate">
                                {itemMeta || itemInfo?.designation || 'Alumni'}
                              </p>
                            </div>
                            {isLoadingThisItem ? (
                              <Spinner size={13} className="animate-spin text-accent shrink-0" />
                            ) : itemScore !== null ? (
                              <span className="shrink-0 font-mono text-[9px] font-extrabold text-accent bg-accent-light border border-accent/20 rounded-full px-2 py-0.5">
                                {itemScore}% Match
                              </span>
                            ) : null}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-[10px] text-slate-400 font-semibold">
                    No similar donors found.
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );
}
